<template>
    <DataTable
        :header="$t('productsSoldBasketAnalysis')"
        :fields="fields"
        :items="itemsFiltered"
        :filter="filter"
        :loading="request.getLoading()"
        :error="request.getError()"
        class="mb-4"
        show-export-button
    >
        <template #headerSide>
            <div class="d-flex gap-3 flex-column">
                <div class="d-flex gap-3 justify-content-end align-items-center">
                    <b-form-checkbox v-model="compareWithPreviousRange" switch>{{ $t('compareWithPreviousRange') }}</b-form-checkbox>
                    <b-form-select
                        v-model="conversionTime"
                        :options="optionsConversionTime"
                        :disabled="request.getLoading()"
                        class="width-200"
                    />
                    <b-form-select
                        v-model="brand"
                        :options="optionsBrand"
                        :disabled="request.getLoading()"
                        :add-null-option="{ null: null, text: $t('allBrands') }"
                        class="width-200"
                    />
                </div>
                <div class="ml-auto d-flex gap-3 justify-content-end align-items-center">
                    <b-form-input v-model="filter" class="width-200" :placeholder="$t('search')" :disabled="request.getLoading()" />
                </div>
            </div>
        </template>
        <template #cell(quantity)="{item}">
            <div>{{ item.quantity }}</div>
            <div v-show="compareWithPreviousRange"><Difference :difference="$format.percent(item.quantityDiff)" /></div>
        </template>
        <template #cell(amount)="{item}">
            <div>{{ $format.currency(item.amount) }}</div>
            <div v-show="compareWithPreviousRange"><Difference :difference="$format.percent(item.amountDiff)" /></div>
        </template>
    </DataTable>
</template>



<script>
    import uniqBy from 'lodash/uniqBy';
    import { calcPercentDiff } from '@/utils/number';
    import DataTable from '@/components/common/DataTable.vue';
    import Difference from '@/components/common/Difference.vue';

    export default {
        name: 'SoldProducts',
        components: {
            DataTable,
            Difference,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        data() {
            return {
                request: this.$req('space/OtherProductsSales'),
                brand: null,
                filter: null,
                compareWithPreviousRange: null,
                conversionTime: null,
            };
        },
        computed: {
            optionsBrand() {
                return uniqBy(
                    (this.request.getData() || []).filter(item => item.brand).map(item => ({ value: item.brand, text: item.brand })),
                    'value'
                );
            },
            optionsConversionTime() {
                return [
                    { value: null, text: this.$t('allConversions') },
                    { value: 24, text: this.$t('upTo24Hours') },
                ];
            },
            items() {
                return (this.request.getData() || []).map(item => ({
                    ...item,
                    quantityDiff: calcPercentDiff(item.quantity, item.quantityCompare),
                    amountDiff: calcPercentDiff(item.amount, item.amountCompare),
                }));
            },
            itemsFiltered() {
                const items = this.items;
                if (this.brand) return items.filter(item => item.brand === this.brand);
                return items;
            },
            fields() {
                return [
                    {
                        key: 'name',
                        label: this.$t('product'),
                        sortable: true,
                        exportable: true,
                        tdClass: '',
                        thClass: '',
                    },
                    {
                        key: 'sku',
                        label: this.$t('model'),
                        sortable: true,
                        exportable: true,
                        tdClass: '',
                        thClass: '',
                    },
                    {
                        key: 'brand',
                        label: this.$t('brand'),
                        sortable: true,
                        exportable: true,
                        tdClass: '',
                        thClass: '',
                    },
                    {
                        key: 'quantity',
                        label: this.$t('quantity2'),
                        sortable: true,
                        total: true,
                        formatter: val => this.$format.number(val),
                        exportable: true,
                        tdClass: 'text-right text-nowrap',
                        thClass: 'text-right',
                    },
                    {
                        key: 'quantityDiff',
                        label: `${this.$t('quantity2')} (% ${this.$t('change2').toLowerCase()})`,
                        sortable: true,
                        exportable: true,
                        show: false,
                        formatter: val => this.$format.number(val),
                        tdClass: 'text-right text-nowrap',
                        thClass: 'text-right',
                    },
                    {
                        key: 'amount',
                        label: this.$t('amount'),
                        sortable: true,
                        total: true,
                        formatter: val => this.$format.number(val),
                        totalFormatter: val => this.$format.currency(val),
                        exportable: true,
                        tdClass: 'text-right text-nowrap',
                        thClass: 'text-right',
                    },
                    {
                        key: 'amountDiff',
                        label: `${this.$t('amount')} (% ${this.$t('change2').toLowerCase()})`,
                        sortable: true,
                        exportable: true,
                        show: false,
                        formatter: val => this.$format.number(val),
                        tdClass: 'text-right text-nowrap',
                        thClass: 'text-right',
                    },
                ];
            },
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },
            timeRange() {
                return this.parentComponent.timeRange;
            },
            apiParams() {
                const urlParams = { spaceId: this.spaceId };
                const { startDate, endDate, compareStartDate, compareEndDate } = this.timeRange;
                const requestParams = { startDate, endDate, compareStartDate, compareEndDate };
                if (this.conversionTime) requestParams.conversionTime = this.conversionTime;
                return { urlParams, requestParams };
            },
        },
        watch: {
            apiParams: {
                handler(newVal, oldVal) {
                    if (JSON.stringify(newVal || {}) !== JSON.stringify(oldVal || {})) {
                        this.loadData();
                    }
                },
                immediate: true,
            },
        },
        methods: {
            calcPercentDiff,
            loadData() {
                this.fetchData();
            },
            fetchData() {
                this.request.get(this.apiParams);
            },
        },
    };
</script>
